<script>
import Popper from 'popper.js';
import Toolbar from '@/components/ui/Toolbar';

// Get the closest table related to the element that is (or is inside) the editable region
function getTable(el) {
  const region = el.closest('.cms-editable');
  const table = el.closest('table');

  if (table === region) return table;

  if (region.contains(table)) {
    return table;
  }

  return null;
}

export default {
  name: 'TableToolbar',
  components: {
    Toolbar
  },
  props: {
    editor: Object
  },
  data() {
    return {
      active: false,
      popper: null,
      position: 'top'
    };
  },
  watch: {
    editor() {
      if (!this.editor) return;
      if (!this.popper) {
        this.popper = new Popper(this.editor.getRootElement(), this.$refs.toolbar.$el, {
          placement: 'top-start',
          removeOnDestroy: true,
          modifiers: {
            flip: {
              enabled: true
            },
            offset: {
              enabled: true,
              offset: '0px, 16px'
            },
            preventOverflow: {
              enabled: true
            },
            hide: {
              enabled: true
            }
          },
          onUpdate: data => {
            this.position = data.placement === 'bottom-start' ? 'bottom' : 'top';
          }
        });
      }

      // Listen for selection change on the editor
      this.editor.on('change', () => this.handleSelectionChange());
      this.handleSelectionChange();
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.cancelMouseDown);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.cancelMouseDown);
  },
  methods: {
    // Prevents toolbar clicks from removing focus from the editor
    cancelMouseDown(event) {
      if (event.target.closest('.toolbar, .el-dropdown-menu')) {
        event.preventDefault();
      }
    },

    deleteTable() {
      const editor = this.editor;
      const table = getTable(editor.getSelectedElement());
      if (table) {
        editor.removeElement(table);
      }
    },

    // Updates the toolbar when the selection changes (e.g. on NodeChange)
    handleSelectionChange() {
      const editor = this.editor;
      if (!editor) return;

      const el = editor.getSelectedElement();
      const table = el ? getTable(el) : null;

      if (table) {
        this.active = true;
        this.popper.reference = table;
        this.popper.scheduleUpdate();
      } else {
        this.active = false;
      }
    }
  }
};
</script>

<template>
  <toolbar
    ref="toolbar"
    :hidden="!editor || !active"
    :position="position"
    :buttons="[
      [
        {
          text: $t('Row'),
          menu: [
            {
              text: $t('Insert Row Before'),
              icon: 'el-icon-fa-arrow-to-top',
              click: () => editor.insertTableRow('before')
            },
            {
              text: $t('Insert Row After'),
              icon: 'el-icon-fa-arrow-to-bottom',
              click: () => editor.insertTableRow('after')
            },
            { text: $t('Delete'), icon: 'el-icon-fa-trash', click: () => editor.deleteTableRow(), divided: true }
          ]
        },
        {
          text: $t('Column'),
          menu: [
            {
              text: $t('Insert Column Before'),
              icon: 'el-icon-fa-arrow-to-left',
              click: () => editor.insertTableColumn('before')
            },
            {
              text: $t('Insert Column After'),
              icon: 'el-icon-fa-arrow-to-right',
              click: () => editor.insertTableColumn('after')
            },
            { text: $t('Delete'), icon: 'el-icon-fa-trash', click: () => editor.deleteTableColumn(), divided: true }
          ]
        },
        {
          text: $t('Cell'),
          menu: [
            { text: $t('Toggle Header Cells'), click: () => editor.toggleTableHeadersCells() },
            { text: $t('Merge Cells'), click: () => editor.mergeTableCells(), divided: true },
            { text: $t('Split Cells'), click: () => editor.splitTableCells() }
          ]
        },
        { icon: 'el-icon-fa-trash', title: $t('Delete'), click: () => deleteTable() }
      ]
    ]"
    class="table-toolbar"
  />
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.table-toolbar {
  position: absolute;
  z-index: $--index-popper + 3;
}
</style>
